import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BarChart3, Globe, Monitor, MapPin, ExternalLink, Users, ArrowLeft, Clock, Smartphone, Laptop, Tablet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { getAnalytics, verifyAdmin } from '@/lib/api';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) { navigate('/admin/login'); return; }
      try { await verifyAdmin(); } catch { navigate('/admin/login'); }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await getAnalytics(days);
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch analytics:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const DeviceIcon = ({ device }) => {
    if (device === 'Mobile') return <Smartphone className="h-3.5 w-3.5" />;
    if (device === 'Tablet') return <Tablet className="h-3.5 w-3.5" />;
    return <Laptop className="h-3.5 w-3.5" />;
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  const maxDaily = data?.daily_views?.length ? Math.max(...data.daily_views.map(d => d.views)) : 1;

  return (
    <div className="min-h-screen bg-[#faf9f7]" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="font-heading text-xl font-semibold text-gray-900">Visitor Analytics</h1>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="analytics-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-xl border border-gray-100 bg-white" data-testid="analytics-total-views">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs font-body">Total Views</span>
              </div>
              <p className="font-heading text-3xl font-semibold text-gray-900">{data?.total_views?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border border-gray-100 bg-white" data-testid="analytics-unique-visitors">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs font-body">Unique Visitors</span>
              </div>
              <p className="font-heading text-3xl font-semibold text-gray-900">{data?.unique_visitors?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border border-gray-100 bg-white" data-testid="analytics-registrations">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs font-body">Registrations</span>
              </div>
              <p className="font-heading text-3xl font-semibold text-gray-900">{data?.total_registrations?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl border border-gray-100 bg-white" data-testid="analytics-paid">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-xs font-body">Paid Tickets</span>
              </div>
              <p className="font-heading text-3xl font-semibold text-gray-900">{data?.paid_registrations?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Daily Views Chart */}
        {data?.daily_views?.length > 0 && (
          <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader className="px-4 sm:px-6 py-4">
              <CardTitle className="font-heading text-base font-semibold text-gray-900">Daily Views</CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              <div className="flex items-end gap-1 h-40">
                {data.daily_views.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.views} views`}>
                    <span className="text-[10px] text-gray-400 font-body">{d.views}</span>
                    <div
                      className="w-full bg-[#1a2332] rounded-sm min-h-[2px] transition-all duration-300"
                      style={{ height: `${(d.views / maxDaily) * 120}px` }}
                    />
                    <span className="text-[8px] text-gray-400 font-body whitespace-nowrap">
                      {d.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Countries */}
          <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader className="px-4 sm:px-6 py-4">
              <CardTitle className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                <Globe className="h-4 w-4" /> Countries
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              {data?.top_countries?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_countries.map((c, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm font-body text-gray-700">{c.country}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#1a2332] rounded-full"
                            style={{ width: `${(c.views / (data.top_countries[0]?.views || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-body font-medium text-gray-900 w-8 text-right">{c.views}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Cities */}
          <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader className="px-4 sm:px-6 py-4">
              <CardTitle className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Cities
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              {data?.top_cities?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_cities.map((c, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm font-body text-gray-700">{c.city}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#1a2332] rounded-full"
                            style={{ width: `${(c.views / (data.top_cities[0]?.views || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-body font-medium text-gray-900 w-8 text-right">{c.views}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Pages */}
          <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader className="px-4 sm:px-6 py-4">
              <CardTitle className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                <ExternalLink className="h-4 w-4" /> Top Pages
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              {data?.top_pages?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_pages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm font-body text-gray-700 truncate max-w-[200px]">{p.page}</span>
                      <span className="text-sm font-body font-medium text-gray-900">{p.views}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Devices & Browsers */}
          <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader className="px-4 sm:px-6 py-4">
              <CardTitle className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                <Monitor className="h-4 w-4" /> Devices & Browsers
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-400 font-body uppercase tracking-wider mb-2">Devices</p>
                  <div className="flex gap-3">
                    {data?.devices && Object.entries(data.devices).map(([device, count]) => (
                      <div key={device} className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-full">
                        <DeviceIcon device={device} />
                        <span className="text-xs font-body text-gray-700">{device}</span>
                        <span className="text-xs font-body font-semibold text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-gray-400 font-body uppercase tracking-wider mb-2">Browsers</p>
                  <div className="flex flex-wrap gap-2">
                    {data?.browsers && Object.entries(data.browsers).map(([browser, count]) => (
                      <Badge key={browser} variant="secondary" className="text-xs rounded-full">
                        {browser}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Traffic Sources */}
          <Card className="rounded-xl border border-gray-100 bg-white lg:col-span-2">
            <CardHeader className="px-4 sm:px-6 py-4">
              <CardTitle className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                <ExternalLink className="h-4 w-4" /> Traffic Sources
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4">
              {data?.top_referrers?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_referrers.map((r, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm font-body text-gray-700">{r.source}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#1a2332] rounded-full"
                            style={{ width: `${(r.views / (data.top_referrers[0]?.views || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-body font-medium text-gray-900 w-8 text-right">{r.views}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Visitors Table */}
        <Card className="rounded-xl border border-gray-100 bg-white">
          <CardHeader className="px-4 sm:px-6 py-4">
            <CardTitle className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Recent Visitors
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-4">
            <div className="overflow-x-auto">
              <Table data-testid="analytics-recent-visitors-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-4 sm:pl-6">Time</TableHead>
                    <TableHead>Page</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Browser</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="pr-4 sm:pr-6">ISP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.recent_visitors?.map((v, i) => (
                    <TableRow key={i}>
                      <TableCell className="pl-4 sm:pl-6 text-xs text-gray-500 whitespace-nowrap">
                        {formatTimestamp(v.timestamp)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-700 max-w-[150px] truncate">{v.page}</TableCell>
                      <TableCell className="text-sm text-gray-700 whitespace-nowrap">
                        {v.city}, {v.country}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DeviceIcon device={v.device} />
                          <span className="text-xs text-gray-600">{v.device}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{v.browser}</TableCell>
                      <TableCell className="text-xs text-gray-500 max-w-[120px] truncate">{v.referrer || 'Direct'}</TableCell>
                      <TableCell className="pr-4 sm:pr-6 text-xs text-gray-500 max-w-[150px] truncate">{v.isp}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
